import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  FormControlLabel,
  Checkbox,
  Box,
  Avatar,
  useTheme,
  useMediaQuery,
  InputAdornment,
  Typography
} from '@mui/material';
import { Category as CategoryIcon } from '@mui/icons-material';
import * as Icons from '@mui/icons-material';

import { Despesa, DespesasFormData, Category } from '../../types';
import expenseService from '../../services/despesasService';
import EscritorioSelect from '../selects/EscritorioSelect';
import { formatarValorOnInput } from '../../utils/formatToView';

interface DespesasFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  selectedExpense?: Despesa | null;
  categories: Category[];
}

// Função para obter data atual no formato YYYY-MM-DD sem problemas de fuso horário
const getCurrentDate = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const DespesasForm: React.FC<DespesasFormProps> = ({
  open,
  onClose,
  onSuccess,
  selectedExpense,
  categories
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [formData, setFormData] = useState<DespesasFormData>({
    descricao: '',
    escritorio_id: 0, // Será preenchido com o escritório do usuário logado
    categoria: '',
    tipo: 'fixa',
    valor: 0,
    data_vencimento: getCurrentDate(),
    observacoes: '',
    recorrente: false,
    num_documento: '',
    forma_cobranca: undefined
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [valorFormatado, setValorFormatado] = useState('0,00');

  // Função auxiliar para criar Date a partir de string YYYY-MM-DD sem problemas de fuso horário
  const criarDataSegura = (dataString: string): Date | null => {
    if (!dataString || dataString === '') return null;
    
    try {
      // Separar os componentes da data
      const [year, month, day] = dataString.split('-').map(num => parseInt(num, 10));
      
      // Validar se os valores são válidos
      if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
      if (year < 1900 || year > 2100) return null;
      if (month < 1 || month > 12) return null;
      if (day < 1 || day > 31) return null;
      
      // Criar Date usando construtor com componentes (evita problemas de fuso horário)
      const date = new Date(year, month - 1, day); // month é 0-based
      
      // Verificar se a data criada é válida
      if (isNaN(date.getTime())) return null;
      
      // Verificar se os componentes correspondem (previne datas como 31/02)
      if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
        return null;
      }
      
      return date;
    } catch (error) {
      console.error('Erro ao criar data segura:', error);
      return null;
    }
  };

  // Função auxiliar para formatar Date para string YYYY-MM-DD
  const formatarDataSegura = (date: Date): string => {
    if (!date || isNaN(date.getTime())) return '';
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  };

  // Função para formatar valor para moeda brasileira
  const formatarMoeda = (valor: number): string => {
    return valor.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Função para converter string formatada para número
  const parsearMoeda = (valorFormatado: string): number => {
    // Remove todos os caracteres que não são números, vírgula ou ponto
    const numeroLimpo = valorFormatado.replace(/[^\d,]/g, '');
    // Substitui vírgula por ponto para conversão
    const numeroComPonto = numeroLimpo.replace(',', '.');
    const numero = parseFloat(numeroComPonto) || 0;
    return numero;
  };  

  // Handler para mudança do campo valor
  const handleValorChange = (valor: string) => {
    const valorFormatado = formatarValorOnInput(valor);
    const valorNumerico = parsearMoeda(valorFormatado);
    
    setValorFormatado(valorFormatado);
    setFormData(prev => ({ ...prev, valor: valorNumerico }));
  };

  // Resetar formulário quando abrir/fechar
  useEffect(() => {
    if (open) {
      if (selectedExpense) {
        // Validar e formatar data de vencimento
        let dataVencimento = '';
        if (selectedExpense.data_vencimento) {
          try {
            // Primeiro tentar como string já formatada (YYYY-MM-DD)
            if (typeof selectedExpense.data_vencimento === 'string' && selectedExpense.data_vencimento.match(/^\d{4}-\d{2}-\d{2}$/)) {
              dataVencimento = selectedExpense.data_vencimento;
            } else {
              // Se não, tentar converter de ISO ou outro formato
              const date = new Date(selectedExpense.data_vencimento);
              if (!isNaN(date.getTime())) {
                dataVencimento = formatarDataSegura(date);
              }
            }
          } catch (error) {
            console.error('Erro ao processar data de vencimento:', error);
            dataVencimento = '';
          }
        }

        setFormData({
          descricao: selectedExpense.descricao,
          categoria: selectedExpense.categoria,
          tipo: selectedExpense.tipo,
          valor: selectedExpense.valor,
          data_vencimento: dataVencimento,
          data_pagamento: selectedExpense.data_pagamento,
          observacoes: selectedExpense.observacoes || '',
          recorrente: selectedExpense.recorrente,
          frequencia_recorrencia: selectedExpense.frequencia_recorrencia,
          num_documento: selectedExpense.num_documento || '',
          forma_cobranca: selectedExpense.forma_cobranca || undefined,
          escritorio_id: selectedExpense.escritorio_id
        });
        setValorFormatado(formatarMoeda(selectedExpense.valor));
      } else {
        resetFormData();
      }
      setErrors({});
    }
  }, [open, selectedExpense]);

  const resetFormData = () => {
    setFormData({
      descricao: '',
      categoria: '',
      tipo: 'fixa',
      valor: 0,
      data_vencimento: getCurrentDate(),
      observacoes: '',
      recorrente: false,
      num_documento: '',
      forma_cobranca: undefined,
      escritorio_id: 0
    });
    setValorFormatado('0,00');
  };

  // Validações
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.escritorio_id) {
      newErrors.escritorio_id = 'Escritório é obrigatório';
    }

    if (!formData.descricao.trim()) {
      newErrors.descricao = 'Descrição é obrigatória';
    }

    if (!formData.categoria) {
      newErrors.categoria = 'Categoria é obrigatória';
    }

    if (!formData.valor || formData.valor <= 0) {
      newErrors.valor = 'Valor deve ser maior que zero';
    }

    if (!formData.data_vencimento) {
      newErrors.data_vencimento = 'Data de vencimento é obrigatória';
    }

    if (formData.recorrente && !formData.frequencia_recorrencia) {
      newErrors.frequencia_recorrencia = 'Frequência é obrigatória para despesas recorrentes';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submissão do formulário
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      if (selectedExpense) {
        const response = await expenseService.atualizarDespesa(selectedExpense.id, formData);
        if (response.success) {
          onSuccess();
          onClose();
        }
      } else {
        const response = await expenseService.cadastrarDespesa(formData);
        if (response.success) {
          onSuccess();
          onClose();
        }
      }
    } catch (error: any) {
      console.error('Erro ao salvar despesa:', error);
      setErrors({ submit: error.message || 'Erro ao salvar despesa' });
    } finally {
      setLoading(false);
    }
  };

  // Buscar categoria por código
  const getCategoryByCode = (codigo: string): Category | undefined => {
    return categories.find(cat => cat.codigo === codigo);
  };

  // Renderizar ícone de categoria
  const renderCategoryIcon = (categoria: string, size: 'small' | 'medium' = 'small') => {
    const categoryData = getCategoryByCode(categoria);
    if (categoryData?.icone) {
      const IconComponent = (Icons as any)[categoryData.icone] || Icons.Category;
      return <IconComponent fontSize={size} />;
    }
    return <CategoryIcon fontSize={size} />;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle sx={{ p: 1.5 }}>
        <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
          {selectedExpense ? 'Editar Despesa' : 'Nova Despesa'}
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ p: 1.5 }}>
        <Grid container spacing={1.5} sx={{ mt: 0.5 }}>        
          
          {/* Escritório */}
          <Grid item xs={12} sm={12}>
            <EscritorioSelect
              value={formData.escritorio_id || null}
              onChange={(value) => setFormData(prev => ({ ...prev, escritorio_id: value as number }))}
              label="Escritório *"
              size="small"
              required={true}
              allowClear={false}
              multiple={false}
              onlyActive={true}
              error={!!errors.escritorio_id}
              helperText={errors.escritorio_id}
            />
          </Grid>
          
          {/* Categoria */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!errors.categoria}>
              <InputLabel>Categoria *</InputLabel>
              <Select
                value={formData.categoria}
                label="Categoria *"
                onChange={(e) => setFormData(prev => ({ ...prev, categoria: e.target.value }))}
                size="small"
              >
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.codigo}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar
                        sx={{
                          width: 20,
                          height: 20,
                          bgcolor: category.cor || '#1976d2',
                          fontSize: '0.75rem'
                        }}
                      >
                        {renderCategoryIcon(category.codigo, 'small')}
                      </Avatar>
                      {category.nome}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              {errors.categoria && (
                <Box component="span" sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5 }}>
                  {errors.categoria}
                </Box>
              )}
            </FormControl>
          </Grid>
          
          {/* Tipo */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Tipo</InputLabel>
              <Select
                value={formData.tipo}
                label="Tipo"
                onChange={(e) => setFormData(prev => ({ ...prev, tipo: e.target.value as any }))}
                size="small"
              >
                <MenuItem value="fixa">Fixa</MenuItem>
                <MenuItem value="variavel">Variável</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Forma de Cobrança */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Forma de Cobrança</InputLabel>
              <Select
                value={formData.forma_cobranca || ''}
                label="Forma de Cobrança"
                onChange={(e) => setFormData(prev => ({ ...prev, forma_cobranca: e.target.value as any || undefined }))}
                size="small"
              >
                <MenuItem value="">Selecione...</MenuItem>
                <MenuItem value="boleto">Boleto</MenuItem>
                <MenuItem value="cartao_credito">Cartão de Crédito</MenuItem>
                <MenuItem value="cartao_debito">Cartão de Débito</MenuItem>
                <MenuItem value="cheque">Cheque</MenuItem>
                <MenuItem value="debito_automatico">Débito Automático</MenuItem>
                <MenuItem value="deposito">Depósito</MenuItem>
                <MenuItem value="dinheiro">Dinheiro</MenuItem>
                <MenuItem value="pix">PIX</MenuItem>
                <MenuItem value="promissoria">Promissória</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {/* Número do Documento */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Número do Documento"
              value={formData.num_documento || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, num_documento: e.target.value }))}
              size="small"
              placeholder="Ex: 001/2025, NF-123456"
            />
          </Grid>

          {/* Data de Vencimento */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Data de Vencimento"
              type="date"
              value={formData.data_vencimento}
              onChange={(e) => setFormData(prev => ({ ...prev, data_vencimento: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              error={!!errors.data_vencimento}
              helperText={errors.data_vencimento}
              required
              size="small"
            />
          </Grid>

          {/* Valor */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Valor"
              type="text"
              value={valorFormatado}
              onChange={(e) => handleValorChange(e.target.value)}
              onBlur={(e) => handleValorChange(e.target.value)}
              error={!!errors.valor}
              helperText={errors.valor}
              required
              placeholder="R$ 0,00"
              size="small"
              InputProps={{
                startAdornment: <InputAdornment position="start">R$</InputAdornment>,
              }}
            />
          </Grid>

          {/* Descrição */}
          <Grid item xs={12} sm={12}>
            <TextField
              fullWidth
              label="Descrição"
              value={formData.descricao}
              onChange={(e) => setFormData(prev => ({ ...prev, descricao: e.target.value }))}
              error={!!errors.descricao}
              helperText={errors.descricao}
              required
              size="small"
            />
          </Grid>
          
          
          
          
          {/* Data de Pagamento (apenas para edição) */}
          {selectedExpense && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Data de Pagamento"
                type="date"
                value={formData.data_pagamento || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, data_pagamento: e.target.value || undefined }))}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
          )}
          
          {/* Observações */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Observações"
              multiline
              rows={3}
              value={formData.observacoes}
              onChange={(e) => setFormData(prev => ({ ...prev, observacoes: e.target.value }))}
              size="small"
            />
          </Grid>
          
          {/* Despesa Recorrente */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.recorrente}
                  onChange={(e) => setFormData(prev => ({ ...prev, recorrente: e.target.checked }))}
                  size="small"
                />
              }
              label="Despesa recorrente"
            />
          </Grid>
          
          {/* Frequência (apenas se recorrente) */}
          {formData.recorrente && (
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.frequencia_recorrencia}>
                <InputLabel>Frequência *</InputLabel>
                <Select
                  value={formData.frequencia_recorrencia || ''}
                  label="Frequência *"
                  size="small"
                  onChange={(e) => setFormData(prev => ({ ...prev, frequencia_recorrencia: e.target.value as any }))}
                >
                  <MenuItem value="mensal">Mensal</MenuItem>
                  <MenuItem value="bimestral">Bimestral</MenuItem>
                  <MenuItem value="trimestral">Trimestral</MenuItem>
                  <MenuItem value="semestral">Semestral</MenuItem>
                  <MenuItem value="anual">Anual</MenuItem>
                </Select>
                {errors.frequencia_recorrencia && (
                  <Box component="span" sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5 }}>
                    {errors.frequencia_recorrencia}
                  </Box>
                )}
              </FormControl>
            </Grid>
          )}

          {/* Erro de submissão */}
          {errors.submit && (
            <Grid item xs={12}>
              <Box sx={{ color: 'error.main', fontSize: '0.875rem' }}>
                {errors.submit}
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ p: 1.5 }}>
        <Button onClick={onClose} disabled={loading} size="small" sx={{ fontSize: '0.8rem' }}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.escritorio_id || !formData.descricao || !formData.valor || !formData.data_vencimento || !formData.categoria}
          size="small"
          sx={{ fontSize: '0.8rem' }}
        >
          {loading ? 'Salvando...' : (selectedExpense ? 'Atualizar' : 'Criar')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DespesasForm; 